import {
  createTemplateField,
  normalizeFieldKey,
  parseKeyFromInput,
} from '../extensions/fieldTokenEditorUtils';

const EMPTY_CONTENT = { type: 'doc', content: [{ type: 'paragraph' }] };

function normalizeContent(raw) {
  if (raw == null) return EMPTY_CONTENT;
  if (typeof raw === 'object' && raw.type === 'doc' && Array.isArray(raw.content)) {
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.type === 'doc' && Array.isArray(parsed.content)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
  }
  return EMPTY_CONTENT;
}

function collectFieldTokensFromContent(content) {
  const tokens = [];

  const walk = (nodes) => {
    (nodes ?? []).forEach((node) => {
      if (node?.type === 'fieldToken') {
        tokens.push({
          uuid: node.attrs?.uuid ?? null,
          key: node.attrs?.key ?? '',
        });
      }
      if (Array.isArray(node?.content)) {
        walk(node.content);
      }
    });
  };

  walk(content?.content);
  return tokens;
}

function createFieldFromToken(token, overrides = {}) {
  const key = normalizeFieldKey(token?.key);
  if (!key) return null;

  const field = createTemplateField(key, overrides.type ?? 'text');
  if (token?.uuid) {
    field.uuid = String(token.uuid);
  }

  if (overrides.fieldName != null) {
    field.fieldName = String(overrides.fieldName);
  }
  if (overrides.obrigatorio != null) {
    field.obrigatorio = Boolean(overrides.obrigatorio);
  }

  return field;
}

/**
 * Map GET /templates/{id} response into editor bootstrap state.
 */
export function hydrateTemplateEditorState(template) {
  const content = normalizeContent(template?.content);
  const tokens = collectFieldTokensFromContent(content);
  const tokensByKey = new Map();

  tokens.forEach((token) => {
    const key = normalizeFieldKey(token.key);
    if (!key || tokensByKey.has(key)) return;
    tokensByKey.set(key, token);
  });

  const fieldsByKey = new Map();
  const apiFields = Array.isArray(template?.fields) ? template.fields : [];

  apiFields.forEach((apiField) => {
    const key = normalizeFieldKey(parseKeyFromInput(apiField?.key ?? ''));
    if (!key) return;

    const token = tokensByKey.get(key);
    const field = createFieldFromToken(token ?? { key }, {
      type: apiField?.type || 'text',
      fieldName: apiField?.name ?? apiField?.fieldName ?? '',
      obrigatorio: apiField?.required ?? apiField?.obrigatorio,
    });

    if (field) {
      fieldsByKey.set(key, field);
    }
  });

  tokens.forEach((token) => {
    const key = normalizeFieldKey(token.key);
    if (!key || fieldsByKey.has(key)) return;

    const field = createFieldFromToken(token);
    if (field) {
      fieldsByKey.set(key, field);
    }
  });

  return {
    id: String(template?.id ?? template?.template_id ?? ''),
    name: String(template?.name ?? '').trim(),
    content,
    fields: [...fieldsByKey.values()],
  };
}
